import { NumberInputMaskPipe } from '../pipes/number-input-mask.pipe';
import { FleetColumnData } from '../../models/api/column.model';
import { DurationFormatPipe } from '../pipes/duration-format.pipe';
import {
  InternalVehicleStatus,
  VehicleStatuses
} from '../../models/vehicle-statuses.model';
import {
  Ext2,
  Icon,
  Position,
  Sparam
} from '../../models/api/vehicle-position.model';
import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';

enum EXT_ONOFF {
  ON = 'Włączony',
  OFF = 'Wyłączony',
  OPEN = 'Otwarte',
  CLOSE = 'Zamknięte',
  WORK = 'Praca',
  STOP = 'Postój',
}

@Injectable({
  providedIn: 'root'
})
export class VehicleCardService {
  temperatuesColumns = [
    {
      temperature: 'EXT_DST1',
      humidity: 'EXT_DSTB16_HUMIDITY'
    },
    {
      temperature: 'EXT_DST2',
      humidity: 'EXT_DSTB17_HUMIDITY'
    },
    {
      temperature: 'EXT_DST3',
      humidity: 'EXT_DSTB18_HUMIDITY'
    },
    {
      temperature: 'EXT_DST4',
      humidity: 'EXT_DSTB19_HUMIDITY',
    },
    {
      temperature: 'EXT_DST5',
      humidity: 'EXT_DSTB20_HUMIDITY'
    }
  ];

  formattedExtColumns: string[] = [
    'EXT_DSF36',
    'EXT_DSF37',
    'EXT_DEGEOCODE',
    'EXT_CAN102',
    'CAN_DISTANCE',
    'EXT_WORKTIME',
    'EXT_DST1',
    'EXT_DST2',
    'EXT_DST3',
    'EXT_DST4',
    'EXT_DST5',
    'EXT_DSTB16_HUMIDITY',
    'EXT_DSTB17_HUMIDITY',
    'EXT_DSTB18_HUMIDITY',
    'EXT_DSTB19_HUMIDITY',
    'EXT_DSTB20_HUMIDITY',
    'EXT_Stan_Zasil'
  ];

  constructor(
    private durationPipe: DurationFormatPipe,
    private datePipe: DatePipe,
    private numberInputMaskPipe: NumberInputMaskPipe,
  ) {
  }



  prepareAdditionalInfo(vehicle: Position): any[] {
    const infos = [];
    const driver = this.getInfo(vehicle, 'EXT_kierowca');
    if (driver && driver.value !== '---') {
      infos.push(driver);
    }
    for (const device of this.temperatuesColumns) {
      const temperature = this.getInfo(vehicle, device.temperature, {
        replaceString: 'Odłączony'
      });
      infos.push(temperature);
      if (temperature && temperature.value !== 'Odłączony') {
        infos.push(this.getInfo(vehicle, device.humidity));
      }
    }
    infos.push(this.getInfo(vehicle, 'EXT_Stan_Zasil'));
    if (vehicle.battery) {
      infos.push({
        key: 'battery',
        value: vehicle.battery
      });
    }
    console.log(infos);
    return infos.filter((value) => !!value);
  }

  getInfo(
    vehicle: Position,
    key: string,
    options: {
      replaceString?: ((value: any) => string) | string;
      formatAsNumber?: boolean;
      hideWhenVehicleOff?: boolean;
    } = {}
  ): any {
    const value = this.extractDataFromExt(vehicle, key);
    if (value !== undefined) {
      if (options.hideWhenVehicleOff && vehicle.speed === '255') {
        return {
          key: key,
          value: '---'
        };
      }
      if (options.replaceString) {
        if (typeof options.replaceString === 'function') {
          return {
            key: key,
            value: options.replaceString(value)
          };
        } else if (Number.isNaN(parseInt(value))) {
          return {
            key: key,
            value: options.replaceString
          };
        }
      }
      return {
        key: key,
        value: options.formatAsNumber
          ? this.numberInputMaskPipe.transform(value)
          : value
      };
    }
  }

  prepareBaseInfo(vehicle: Position, fleetColumns: FleetColumnData): any[] {
    const infos = [];
    if (
      fleetColumns &&
      fleetColumns.column_visibility_fuel &&
      vehicle &&
      vehicle.fuel != null
    ) {
      infos.push({
        key: 'fuel',
        value: /l/.test(vehicle.fuel) ? vehicle.fuel : vehicle.fuel + ' l'
      });
    }
    infos.push(this.getInfo(vehicle, 'EXT_RideType'));
    const speedLimit = this.extractDataFromExt(vehicle, 'EXT_SPEEDLIMIT');
    if (speedLimit && speedLimit !== '-' && speedLimit !== '---') {
      infos.push({
        key: 'EXT_SPEEDLIMIT',
        value: speedLimit + ' km/h'
      });
    }
    return infos.filter((value) => !!value);
  }

  extractValueFromStats(stat: Sparam): string {
    return this.parseStatValue(stat.name, stat.value, stat.type);
  }

  parseStatValue(key: string, value: string, type?: string): string {
    if (type.includes('date')) {
      if (value) {
        const today = new Date().toDateString();
        const vehicleDay = new Date(parseInt(value, 10) * 1000).toDateString();
        return this.datePipe.transform(
          parseInt(value, 10) * 1000,
          vehicleDay !== today ? 'yyyy-MM-dd HH:mm:ss' : 'HH:mm:ss'
        );
      } else {
        return 'Brak uruchomienia';
      }
    } else if (type.includes('speed')) {
      return `${Math.floor(parseInt(value))} km/h`;
    } else if (type.includes('time')) {
      return this.durationPipe.transform(value, 'hh:mm:ss', 's');
    } else if (type.includes('number')) {
      return value;
    } else if (type.includes('percentage')) {
      return `${value} %`;
    } else if (key.includes('time')) {
      return this.durationPipe.transform(value, 'hh:mm:ss', 's');
    } else if (key.includes('Percentage')) {
      return `${value} %`;
    } return value;
  }

  getTemperatures(vehicle: Position): any[] {
    return this.temperatuesColumns
      .map((column) => {
        const temperature = this.extractDataFromExt(
          vehicle,
          column.temperature
        );
        const temperatureAsInt = Number(temperature);

        if (!Number.isNaN(temperatureAsInt)) {
          const humidity = this.extractDataFromExt(vehicle, column.humidity);
          return {
            id: column.temperature.match(/\d+/),
            temperature: Number.isNaN(temperatureAsInt)
              ? '-'
              : temperature + '°C',
            humidity: Number.isNaN(Number(humidity)) ? '-' : humidity + '%',
            // powerWarning:
            //   Number(this.extractDataFromExt(vehicle, column.power)) < 20,
            isAlarm: Math.random() < 0.5
          };
        } else {
          return null;
        }
      })
      .filter((value) => value);
  }

  getTemperaturesForVehicles(vehicles: Position[]): any[] {
    return vehicles
      .map((vehicle) => {
        return {
          vehicle: vehicle,
          temperatures: this.temperatuesColumns
            .map((column) => {
              const temperature = this.extractDataFromExt(vehicle, column.temperature);
              const humidity = this.extractDataFromExt(vehicle, column.humidity);

              const idFind = column.temperature.match(/\d+/);
              const id = idFind && idFind[0] ? +idFind[0] : null;

              if (temperature && temperature.toLowerCase().includes('odłączenie')) {
                return {
                  id: id,
                  temperature: 'Odłączono od magistrali',
                  humidity: '-',
                  battery: '-',
                };
              }

              const temperatureAsInt = Number(temperature);
              if (!Number.isNaN(temperatureAsInt)) {
                return {
                  id: id,
                  temperature: temperature + '°C',
                  humidity: Number.isNaN(Number(humidity)) ? '-' : humidity + '%',
                };
              }

              return null;
            })
            .filter((value) => value),
        };
      })
      .sort((a, b) => {
        return String(a.vehicle.name).localeCompare(String(b.vehicle.name));
      });
  }

  prepareLocation(vehicle: Position): string {

    return this.extendLocationToVerifiedInfo(vehicle.sat,
      this.extractDataFromExt(vehicle, 'EXT_DEGEOCODE'));
  }

  extendLocationToVerifiedInfo(sat: string, location: string): string {
    // if (typeof sat !== 'undefined' && sat !== null && sat.trim() !== '') {
    //   if (+sat < 3) {
    //     return location + ' (niezweryfikowana)';
    //   }
    // }
    return location;
  }

  extractDataFromExt(
    vehicle: Position,
    extName: string,
    nullValue?: string
  ): string {
    let extObj: Ext2;
    if (vehicle && vehicle.extcolumns) {
      extObj = vehicle.extcolumns.ext.find(
        (metadata) => metadata.name === extName
      );
    }
    return extObj ? extObj.value : nullValue;
  }

  prepareStatus(
    vehicle: Position,
    lastUpdateDuration: number
  ): InternalVehicleStatus {
    const statuses = this.listOfStatuses(vehicle);
    if (statuses.some((stat) => stat.token === VehicleStatuses.POWER_WARNING)) {
      return InternalVehicleStatus.POWER_WARNING;
    } else if (
      statuses.some((stat) => stat.token === VehicleStatuses.POWER_CRITICAL)
    ) {
      return InternalVehicleStatus.POWER_CRITICAL;
    } else {
      const min = 60 * 1000;
      const hour = min * 60;
      const _15m = 15 * min;
      const _4h30m = 4 * hour + 30 * min;
      const _40m = 40 * min;
      const _24h30m = 24 * hour + 30 * min;
      return vehicle && vehicle.speed !== '255'
        ? this.getTimeStatus(_15m, _4h30m, lastUpdateDuration)
        : this.getTimeStatus(_40m, _24h30m, lastUpdateDuration);
    }
  }

  private listOfStatuses(vehicle: Position): Icon[] {
    if (vehicle && vehicle.statusicons && vehicle.statusicons.icon) {
      const status = vehicle.statusicons.icon;
      return Array.isArray(status) ? status : [status];
    } else {
      return [];
    }
  }

  private getTimeStatus(
    firstThreshold: number,
    secondThreshold: number,
    lastUpdateDuration: number
  ): InternalVehicleStatus {
    if (lastUpdateDuration !== undefined) {
      if (lastUpdateDuration <= firstThreshold) {
        return InternalVehicleStatus.OK;
      } else if (lastUpdateDuration <= secondThreshold) {
        return InternalVehicleStatus.DELAYED;
      } else {
        return InternalVehicleStatus.UNAVAILABLE;
      }
    } else {
      return InternalVehicleStatus.NO_DATA;
    }
  }

  prepareOnOff(vehicle: Position): string {
    switch (this.extractDataFromExt(vehicle, 'EXT_ONOFF')) {
      case EXT_ONOFF.ON:
        return 'on';
      case EXT_ONOFF.OFF:
        return 'off';
      case EXT_ONOFF.OPEN:
        return 'open';
      case EXT_ONOFF.CLOSE:
        return 'close';
      case EXT_ONOFF.WORK:
        return 'work';
      case EXT_ONOFF.STOP:
        return 'stop';
      default:
        return undefined;
    }
  }
}
